import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useInvoiceStore } from "../../Store/Invoice";
import {
  AdyenPaymentMethodVerificationStatus,
  AdyenTransactionStatus,
  ShowPaymentResultView
} from "../../Contracts/Transaction";
import { InvoiceStatus } from "../../Contracts/Invoice";
import { usePaymentMethodManagementStore } from "../../Store/PaymentMethodManagement";
import { useTeamStore } from "../../Store/Team";
import MessageBanner from "../../Components/Molecules/PaymentsAppHeader/MessageBanner";
import PaymentForm from "../../Components/Organisms/PaymentForm";
import Lottie from "react-lottie";
import CheckMarkLottie from "../../Assets/ClientPortalLoginPage/CheckMarkLottie.json";
import { PaymentRoutes } from "../../Contracts/Routes";
import LoadingPage from "../LoadingPage";

const CheckoutInvoicePage = () => {
  const { customer_id, invoice_id } = useParams();
  const { formatCurrency } = useTeamStore();
  const [showPaymentResultView, setShowPaymentResultView] = useState<ShowPaymentResultView | null>(
    null
  );

  const [dropInLoading, setDropInLoading] = useState(false);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  const { getCheckoutDetails, getInvoicePdf, checkoutDetailsView, invoicePdfUrl } =
    useInvoiceStore();

  const {
    publicFunctions: {
      createInvoiceCheckoutSession,
      doGiactValidation,
      getTransactionCreateResult,
      createTransactionForInvoice,
      handleBacsPayment
    },
    invoiceCheckoutSessionData
  } = usePaymentMethodManagementStore();

  const initialiseCheckoutPage = useCallback(async () => {
    if (customer_id && invoice_id) {
      await Promise.all([
        getCheckoutDetails(customer_id, invoice_id),
        getInvoicePdf(customer_id, invoice_id)
      ]);
    }
  }, [customer_id, invoice_id, getCheckoutDetails, getInvoicePdf]);

  useEffect(() => {
    const transactionStatus =
      checkoutDetailsView?.transaction_status || showPaymentResultView?.transaction?.status;

    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }

    if (
      (transactionStatus === AdyenTransactionStatus.Processing ||
        transactionStatus === AdyenTransactionStatus.Initiated) &&
      customer_id &&
      invoice_id
    ) {
      pollingInterval.current = setInterval(() => {
        getCheckoutDetails(customer_id, invoice_id);
      }, 3000);
    }

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [
    checkoutDetailsView?.transaction_status,
    showPaymentResultView?.transaction?.status,
    customer_id,
    invoice_id,
    getCheckoutDetails
  ]);

  useEffect(() => {
    initialiseCheckoutPage();
  }, [initialiseCheckoutPage]);

  const allowedPaymentMethods =
    checkoutDetailsView?.payment_method
      ?.filter(
        m => m.active && m.verification_status === AdyenPaymentMethodVerificationStatus.Valid
      )
      .map(m => m.type) || [];

  const showReceiptHandler = () => {
    const transactionId =
      checkoutDetailsView?.transaction_id ?? showPaymentResultView?.transaction.id;

    if (customer_id && transactionId) {
      window.open(
        PaymentRoutes.INVOICE_RECEIPT.replace(":customer_id", customer_id).replace(
          ":transaction_id",
          transactionId.toString()
        ),
        "_blank"
      );
    } else {
      window.alert("Receipt not found!");
    }
  };

  return (
    <>
      <MessageBanner />
      {showPaymentResultView?.is_success ||
      [
        AdyenTransactionStatus.Completed,
        AdyenTransactionStatus.Initiated,
        AdyenTransactionStatus.Processing
      ].includes(checkoutDetailsView?.transaction_status!) ||
      checkoutDetailsView?.invoice_status === InvoiceStatus.Paid ? (
        <div className="flex flex-col items-center h-screen bg-gray-100 p-36">
          <div className="flex items-center absolute left-0 top-0 m-4 space-x-2">
            {checkoutDetailsView?.partner_display_details?.branding_company_logo_s3_url && (
              <img
                src={checkoutDetailsView?.partner_display_details.branding_company_logo_s3_url}
                alt="Company Logo"
                className="max-h-10"
              />
            )}
            <span className="font-semibold">
              {checkoutDetailsView?.partner_display_details?.name}
            </span>
          </div>
          <div className="flex flex-col items-center bg-white px-60 py-12">
            <Lottie
              options={{ animationData: CheckMarkLottie }}
              width="14rem"
              isClickToPauseDisabled
              style={{ cursor: "default" }}
            />
            <div className="text-lime-600 text-2xl font-semibold my-2 text-center">
              {checkoutDetailsView?.transaction_status === AdyenTransactionStatus.Completed
                ? "Payment Successful!"
                : showPaymentResultView?.transaction?.status ===
                      AdyenTransactionStatus.Processing ||
                    showPaymentResultView?.transaction?.status === AdyenTransactionStatus.Initiated
                  ? "Your transaction is processing"
                  : "Your transaction is successful"}
            </div>
            <div className="text-center my-2">
              {showPaymentResultView?.transaction?.status === AdyenTransactionStatus.Completed
                ? "Invoice payment received successfully"
                : showPaymentResultView?.transaction?.status === AdyenTransactionStatus.Processing
                  ? `It will be settled in ${showPaymentResultView?.settlement_days}`
                  : "We have already received your payment for this invoice. Thank you!"}
            </div>
            {checkoutDetailsView?.transaction_status === AdyenTransactionStatus.Completed && (
              <button
                className="mt-3 border rounded px-4 py-2 border-blue-600 hover:bg-blue-100 text-blue-600 hover:scale-105"
                onClick={showReceiptHandler}
              >
                Show Receipt
              </button>
            )}
          </div>
        </div>
      ) : checkoutDetailsView?.invoice_status === InvoiceStatus.Open ||
        checkoutDetailsView?.invoice_status === InvoiceStatus.Overdue ? (
        <div className="grid grid-cols-5 h-screen">
          <div className="md:col-span-3 col-span-5 bg-white-smoke relative">
            <div className="h-full flex flex-col space-y-2">
              <div className="py-4 px-12 flex items-center space-x-4">
                {checkoutDetailsView?.partner_display_details?.branding_company_logo_s3_url && (
                  <img
                    src={checkoutDetailsView?.partner_display_details.branding_company_logo_s3_url}
                    alt="Company Logo"
                    className="max-h-10"
                  />
                )}
                <div className="font-semibold text-lg">
                  {checkoutDetailsView?.partner_display_details?.name}
                </div>
              </div>
              {invoicePdfUrl && (
                <iframe
                  src={`${invoicePdfUrl}#view=FitH`}
                  className="w-full flex-grow px-12"
                  title="PDF Viewer"
                />
              )}
            </div>
          </div>
          <div className="md:col-span-2 col-span-5 p-8 overflow-y-auto">
            <div className="border-b">
              <div className="text-lg font-semibold">Review Payment</div>
              <div className="bg-white-smoke p-4 my-4 rounded">
                {invoiceCheckoutSessionData?.transaction_data.surcharges_applied && (
                  <>
                    <div className="flex justify-between pb-2">
                      <div>Invoice amount</div>

                      <div
                        className={`${
                          dropInLoading ? "animate-pulse bg-gray-200 text-gray-200 rounded-2xl" : ""
                        }`}
                      >
                        {invoiceCheckoutSessionData?.transaction_data.original_amount &&
                          formatCurrency(
                            invoiceCheckoutSessionData.transaction_data.original_amount.value,
                            invoiceCheckoutSessionData.transaction_data.original_amount.currency
                          )}
                      </div>
                    </div>
                    <div className="flex justify-between border-b pb-3 mb-3">
                      <div
                        className={`${
                          dropInLoading ? "animate-pulse bg-gray-200 text-gray-200 rounded-2xl" : ""
                        }`}
                      >
                        {invoiceCheckoutSessionData?.transaction_data.surcharges_fee_description ||
                          "Transaction fee"}
                      </div>
                      <div
                        className={`${
                          dropInLoading ? "animate-pulse bg-gray-200 text-gray-200 rounded-2xl" : ""
                        }`}
                      >
                        {invoiceCheckoutSessionData?.transaction_data.surcharges_fee_amount &&
                          formatCurrency(
                            invoiceCheckoutSessionData.transaction_data.surcharges_fee_amount.value,
                            invoiceCheckoutSessionData.transaction_data.surcharges_fee_amount
                              .currency
                          )}
                      </div>
                    </div>
                  </>
                )}
                <div className="flex justify-between font-semibold">
                  <div>Amount Due</div>
                  <div
                    className={`${
                      dropInLoading ? "animate-pulse bg-gray-200 text-gray-200 rounded-2xl" : ""
                    }`}
                  >
                    {invoiceCheckoutSessionData?.transaction_data.total_amount &&
                      formatCurrency(
                        invoiceCheckoutSessionData.transaction_data.total_amount.value,
                        invoiceCheckoutSessionData.transaction_data.total_amount.currency
                      )}
                  </div>
                </div>
              </div>
            </div>

            <div className="my-4 font-semibold">Select Payment Method</div>

            <PaymentForm
              onChangeDropInLoading={setDropInLoading}
              allowedPaymentMethods={allowedPaymentMethods}
              fetchAdyenSessionForMethod={async method => {
                if (customer_id && invoice_id) {
                  await createInvoiceCheckoutSession(
                    parseInt(customer_id),
                    parseInt(invoice_id),
                    method
                  );
                }
              }}
              sessionData={invoiceCheckoutSessionData?.session}
              callNotifyRoute={async resultCode => {
                if (!invoiceCheckoutSessionData) {
                  console.error("No invoiceCheckoutSessionData found");
                  return Promise.reject(
                    "Unexpected error occured, there is no invoiceCheckoutSessionData"
                  );
                }
                const response = await getTransactionCreateResult(
                  invoiceCheckoutSessionData?.transaction_data.reference,
                  resultCode
                );
                setShowPaymentResultView(response);
              }}
              callBacsPaymentRoute={async (data, transactionReference) => {
                if (!invoice_id) {
                  console.error("Unexpected error occured, there is no invoice_id");
                  return Promise.reject("Unexpected error occured, there is no invoice_id");
                }
                return (await handleBacsPayment(transactionReference, data, parseInt(invoice_id)))
                  .success;
              }}
              callTransactionRoute={async method => {
                if (!invoiceCheckoutSessionData) {
                  console.error("No session data found");
                  return Promise.reject("Unexpected error occured, there is no session data");
                }
                return await createTransactionForInvoice({
                  method,
                  invoice_id: parseInt(invoice_id!),
                  transaction: invoiceCheckoutSessionData.transaction_data
                });
              }}
              doGiactValidation={async (billingAddress, method) => {
                if (!customer_id) {
                  console.error("No customer ID found");
                  return Promise.reject("Unexpected error occured, there is no customerId");
                }
                return await doGiactValidation({
                  customer_id: parseInt(customer_id),
                  billing_address_data: billingAddress,
                  payment_method: method,
                  referrer: window.location.href
                });
              }}
              onPaymentCompleted={() => {
                console.log("Payment Completed");
              }}
            />
          </div>
        </div>
      ) : (
        <div className="w-full h-screen flex items-center justify-center">
          <LoadingPage />
        </div>
      )}
    </>
  );
};

export default CheckoutInvoicePage;
